import React, { useState, useEffect } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDatabase,
  faSearch,
  faSlidersH,
  faXmark,
  faChevronRight,
  faChevronLeft,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const COLS = ["#", "Model Name", "Records", "Created", "Last Modified", ""];

const MasterDataListing = () => {
  const [allModels, setAllModels] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ createdAt: "", updatedAt: "" });
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await func.get(urls.GET_ALL_MODELS_MASTER_DATA_URL);
      if (res?.status === 200) {
        setAllModels(res.data);
        setFiltered(res.data);
      } else {
        toast.error("Failed to load master data");
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleSearch = (val) => {
    setSearchTerm(val);
    const term = val.toLowerCase();
    setFiltered(
      val ? allModels.filter((m) => m.name?.toLowerCase().includes(term)) : allModels
    );
  };

  const applyFilters = () => {
    const setMidnight = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x.getTime(); };
    setFiltered(allModels.filter((m) => {
      const okCreated = filters.createdAt ? setMidnight(m.createdAt) === setMidnight(filters.createdAt) : true;
      const okUpdated = filters.updatedAt ? setMidnight(m.lastModifiedAt) === setMidnight(filters.updatedAt) : true;
      return okCreated && okUpdated;
    }));
    setShowFilter(false);
  };

  const resetFilters = () => {
    setFilters({ createdAt: "", updatedAt: "" });
    setSearchTerm("");
    setFiltered(allModels);
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faDatabase} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">Master Data</h1>
              <p className="text-white/70 text-sm">Manage lookup tables and reference data</p>
            </div>
          </div>
          <button
            onClick={() => setShowFilter(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
          >
            <FontAwesomeIcon icon={faSlidersH} />
            Filters
          </button>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by model name..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {COLS.map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faDatabase} className="text-3xl mb-3 block mx-auto opacity-20" />
                      No models found
                    </td>
                  </tr>
                ) : (
                  filtered.map((m, i) => (
                    <tr
                      key={m.name}
                      className="border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer"
                      onClick={() => navigate(`/master-data-management/modellist/${m.name}`)}
                    >
                      <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{m.name}</td>
                      <td className="px-4 py-3 text-gray-600">{m.noOfRecords ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(m.createdAt)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(m.lastModifiedAt)}</td>
                      <td className="px-4 py-3 text-indigo-600">
                        <FontAwesomeIcon icon={faArrowRight} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Record count */}
        {!loading && (
          <p className="text-white/50 text-xs mt-3 text-right">
            {filtered.length} of {allModels.length} models
          </p>
        )}
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowFilter(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Filter Models</h3>
              <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Created At</label>
                <input
                  type="date"
                  value={filters.createdAt}
                  onChange={(e) => setFilters({ ...filters, createdAt: e.target.value })}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Last Modified At</label>
                <input
                  type="date"
                  value={filters.updatedAt}
                  onChange={(e) => setFilters({ ...filters, updatedAt: e.target.value })}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={resetFilters} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                Reset
              </button>
              <button onClick={applyFilters} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterDataListing;
